import { Link, useLocation, matchPath } from 'react-router-dom';
import {
  TbLayoutDashboard, TbUsers, TbClipboardList, TbHome, TbReceipt, TbUserCircle, TbBarbell,
  TbActivity, TbChartLine, TbUserCheck, TbDeviceTablet,
  TbSettings, TbChartBar, TbMessageCircle, TbDoorEnter,
} from 'react-icons/tb';
import { useAuth } from '../auth/AuthContext';
import { LogoutButton } from '../auth/LogoutButton';
import { Brand } from '../components/Brand';
import { InstallPrompt } from '../pwa/InstallPrompt';

// Order and labels are fixed by the spec ("Menú lateral"); mobile navigation
// uses this as its full source of role routes, labels and icons.
export const NAV_ITEMS = {
  admin: [
    { to: '/admin', label: 'Resumen', icon: TbLayoutDashboard, end: true },
    { to: '/comunidad', label: 'Comunidad', icon: TbMessageCircle },
    { to: '/admin/socios', label: 'Socios', icon: TbUsers },
    { to: '/admin/pagos', label: 'Pagos', icon: TbReceipt },
    { to: '/admin/ingresos', label: 'Ingresos', icon: TbDoorEnter },
    { to: '/admin/planes', label: 'Planes', icon: TbClipboardList },
    { to: '/admin/profesores', label: 'Profesores', icon: TbUserCheck },
    { to: '/admin/ejercicios', label: 'Ejercicios', icon: TbBarbell },
    { to: '/admin/comunidad/denuncias', label: 'Denuncias de comunidad', icon: TbMessageCircle },
    { to: '/admin/kioscos', label: 'Kioscos', icon: TbDeviceTablet },
    { to: '/admin/reportes', label: 'Reportes', icon: TbChartBar },
    { to: '/admin/configuracion', label: 'Configuración', icon: TbSettings },
  ],
  user: [
    { to: '/mi-cuenta', label: 'Inicio', icon: TbHome, end: true },
    { to: '/comunidad', label: 'Comunidad', icon: TbMessageCircle },
    { to: '/mi-cuenta/rutina', label: 'Mi rutina', icon: TbBarbell },
    { to: '/mi-cuenta/entrenar', label: 'Entrenar', icon: TbActivity },
    { to: '/mi-cuenta/progreso', label: 'Progreso', icon: TbChartLine },
    { to: '/mi-cuenta/cuota', label: 'Mi cuota', icon: TbReceipt },
    { to: '/mi-cuenta/perfil', label: 'Mi perfil', icon: TbUserCircle },
  ],
  coach: [
    { to: '/coach', label: 'Mis socios', icon: TbUsers, end: true, activePaths: ['/coach/socios/:id'] },
    { to: '/coach/ejercicios', label: 'Ejercicios', icon: TbBarbell },
    { to: '/comunidad', label: 'Comunidad', icon: TbMessageCircle },
  ],
};

// Ordered mobile subset of NAV_ITEMS; labels, destinations and icons stay
// sourced from the single role navigation registry above.
export const MOBILE_NAV_ORDER = {
  admin: ['/admin', '/admin/socios', '/admin/pagos'],
  user: ['/mi-cuenta', '/mi-cuenta/rutina', '/mi-cuenta/entrenar', '/comunidad'],
  coach: ['/coach', '/coach/ejercicios', '/comunidad'],
};

const ROLE_LABELS = { admin: 'Recepción', user: 'Alumno', coach: 'Profesor' };

export function isNavItemActive(item, pathname) {
  if (matchPath({ path: item.to, end: item.end === true }, pathname)) return true;
  return (item.activePaths || []).some((path) => Boolean(matchPath({ path, end: true }, pathname)));
}

/**
 * Sidebar content: logo on top, the role's sections in the middle and the
 * signed-in identity + logout at the bottom (spec: "Menú lateral"). Rendered
 * inside the fixed desktop column; `onNavigate` is kept for callers that
 * need to close their own navigation container after a section is chosen.
 */
export function Sidebar({ role, onNavigate }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const items = NAV_ITEMS[role] || [];
  const displayName = (user && (user.username || user.email)) || '';

  return (
    <div className="gym-sidebar">
      <div className="gym-sidebar__brand">
        <Brand tone="dark" />
      </div>

      <nav aria-label="Principal" className="gym-sidebar__nav">
        <ul className="gym-nav">
          {items.map((item) => {
            const { to, label, icon: Icon } = item;
            const isCurrent = isNavItemActive(item, pathname);
            return (
              <li key={to}>
                <Link
                  to={to}
                  onClick={onNavigate}
                  aria-current={isCurrent ? 'page' : undefined}
                  className={isCurrent ? 'gym-nav__link is-active' : 'gym-nav__link'}
                >
                  <Icon className="gym-nav__icon" aria-hidden="true" focusable="false" />
                  <span>{label}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      <InstallPrompt variant="compact" />

      <div className="gym-sidebar__footer">
        <div className="gym-identity">
          <span className="gym-identity__avatar" aria-hidden="true">
            {displayName.trim().charAt(0).toUpperCase() || '?'}
          </span>
          <span className="gym-identity__text">
            <span className="gym-identity__name">{displayName}</span>
            <span className="gym-identity__role">{ROLE_LABELS[role]}</span>
            {(role === 'admin' || role === 'coach') && (
              <Link className="gym-identity__link" to="/cambiar-password">Cambiar contraseña</Link>
            )}
          </span>
        </div>
        <LogoutButton variant="dark" />
      </div>
    </div>
  );
}
